import React, { useState } from "react";
import {
  Select,
  Button,
  Tag,
  Table,
  Typography,
  Card,
  Modal,
  Spin,
  Tabs,
  Row,
  Col
} from "antd";
import { useQuery, useLazyQuery } from "@apollo/client";
import { CloseOutlined } from "@ant-design/icons";
import PeriodSelector from "../components/PeriodSelector";
import GET_MUTUAL_FUNDS from "../graphql/mutual_funds";
import GET_FUND_OVERLAP_PT from "../graphql/fund_overlap_pt";
import GET_FUND_OVERLAP from "../graphql/fund_overlap";
import VennDiagram from "../charts/VennDiagram";
import { getHoldingPeriod } from "../utils/dateUtils";
import "../styles/Insights.css";

const { Title } = Typography;

const PortfolioOverlap = () => {
//   Portfolio tab states
  const defaultPeriod = getHoldingPeriod();
  const [activeTab, setActiveTab] = useState("portfolio");
  const [holdingMonth, setHoldingMonth] = useState(defaultPeriod.holdingMonth);
  const [holdingYear, setHoldingYear] = useState(defaultPeriod.holdingYear);
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [overlapMatrix, setOverlapMatrix] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState(null);
  // Compare tab states
  const[compareHoldingMonth,setCompareHoldingMonth]=useState(defaultPeriod.holdingMonth);
  const[compareHoldingYear,setCompareHoldingYear]=useState(defaultPeriod.holdingYear);
  const[compareFund1,setCompareFund1]=useState(null);
  const[compareFund2,setCompareFund2]=useState(null);
  const[compareLoading,setCompareLoading]=useState(false);
  const[compareResult,setCompareResult]=useState(null);
//   Query
  const { data: fundsData, loading: fundsLoading } = useQuery(GET_MUTUAL_FUNDS);
  const [getFundOverlapPt] = useLazyQuery(GET_FUND_OVERLAP_PT);
  const [getFundOverlap] = useLazyQuery(GET_FUND_OVERLAP);

  const allFunds = fundsData?.getMutualFunds || [];

  const handleFundSelect = (fundId) => {
    const fund = allFunds.find((f) => f.id === fundId);
    if (fund && !selectedFunds.find((f) => f.id === fundId)) {
      setSelectedFunds([...selectedFunds, fund]);
    }
  };

  const handleFundRemove = (fundId) => {
    setSelectedFunds(selectedFunds.filter((f) => f.id !== fundId));
    setOverlapMatrix([]);
  };

  const generateOverlapReport = async () => {
    if (selectedFunds.length < 2) {
      return;
    }

    setLoading(true);
    const matrix = [];
    const n = selectedFunds.length;

    // Generate nC2 pairs
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const fund1 = selectedFunds[i];
        const fund2 = selectedFunds[j];

        try {
          const { data } = await getFundOverlapPt({
            variables: {
              fundId1: fund1.id,
              fundId2: fund2.id,
              holdingMonth,
              holdingYear,
            },
          });

        //   console.log(`Overlap data for ${fund1.name} & ${fund2.name}:`, data);
          

          const overlapPt = data?.getFundOverlapPt ?? 0;

          matrix.push({
            fund1Id: fund1.id,
            fund1Name: fund1.name,
            fund2Id: fund2.id,
            fund2Name: fund2.name,
            overlapPt: typeof overlapPt === 'number' ? overlapPt.toFixed(2) : '0.00',
          });
        } catch (error) {
          console.error(
            `Error fetching overlap for ${fund1.name} & ${fund2.name}:`,
            error
          );
          // When there is no overlap, return 0.00 overlap
          matrix.push({
            fund1Id: fund1.id,
            fund1Name: fund1.name,
            fund2Id: fund2.id,
            fund2Name: fund2.name,
            overlapPt: '0.00',
          });
        }
      }
    }

    setOverlapMatrix(matrix);
    setLoading(false);
  };
  // Overlap renderer color
  const getOverlapColor = (overlap) => {
    const val = parseFloat(overlap);
    if (val <= 10) return "#d4edda"; // light green
    if (val <= 25) return "#fff3cd"; // light yellow
    return "#f8d7da"; // light red
  };
//   Main code for overlap funds tab ...
  const getOverlapLabel=(overlap)=>{
    const val=parseFloat(overlap);
    if(val<=10) return "LOW OVERLAP";
    if(val<=25) return "MEDIUM OVERLAP";
    return "HIGH OVERLAP";
  }
  const handleCompareTwoFunds=async()=>{
    if(!compareFund1 || !compareFund2){
        return;
    }
    setCompareLoading(true);
    try{
        const[overlapPtResponse,overlapResponse]=await Promise.all([
            getFundOverlapPt({
                variables:{
                    fundId1:compareFund1,
                    fundId2:compareFund2,
                    holdingMonth:compareHoldingMonth,
                    holdingYear:compareHoldingYear,
                },
            }),
            getFundOverlap({
                variables:{
                    fundId1:compareFund1,
                    fundId2:compareFund2,
                    holdingMonth:compareHoldingMonth,
                    holdingYear:compareHoldingYear,
                },
            }),
        ]);
        const overlapPt=overlapPtResponse.data?.getFundOverlapPt ?? 0;
        const stocks=overlapResponse.data?.getFundOverlap ?? [];
        const fund1Name =
        allFunds.find((f) => f.id === compareFund1)?.name || "Fund 1";
        const fund2Name =
        allFunds.find((f) => f.id === compareFund2)?.name || "Fund 2";
        setCompareResult({
            fund1Name,
            fund2Name,
            overlapPt: typeof overlapPt==="number" ? overlapPt.toFixed(2) : "0.00",
            stocks,
            stockCount:stocks.length,
        });
    } catch(error){
        console.error("Error fetching overlap details:",error);
        setCompareResult(null);
    }
    setCompareLoading(false);
  };

  const handleCellClick = async (record) => {
    setModalVisible(true);
    setModalData({ loading: true });

    try {
      const { data } = await getFundOverlap({
        variables: {
          fundId1: record.fund1Id,
          fundId2: record.fund2Id,
          holdingMonth,
          holdingYear,
        },
      });

      setModalData({
        loading: false,
        fund1Name: record.fund1Name,
        fund2Name: record.fund2Name,
        overlapPt: record.overlapPt,
        stocks: data?.getFundOverlap || [],
      });
    } catch (error) {
      console.error("Error fetching overlap details:", error);
      setModalData({ loading: false, error: true });
    }
  };

  const columns = [
    {
      title: "Fund 1",
      dataIndex: "fund1Name",
      key: "fund1Name",
      width: "35%",
    },
    {
      title: "Fund 2",
      dataIndex: "fund2Name",
      key: "fund2Name",
      width: "35%",
    },
    {
      title: "Overlap %",
      dataIndex: "overlapPt",
      key: "overlapPt",
      width: "30%",
      align: "center",
      render: (text, record) => (
        <div
          style={{
            backgroundColor: getOverlapColor(text),
            padding: "8px",
            borderRadius: "6px",
            fontWeight: 600,
            cursor: "pointer",
          }}
          onClick={() => handleCellClick(record)}
        >
          {text}%
        </div>
      ),
    },
  ];

  const modalColumns = [
    {
      title: "Stock Name",
      dataIndex: "stockName",
      key: "stockName",
    },
    {
      title: `% in ${modalData?.fund1Name || "Fund 1"}`,
      dataIndex: "ptHoldingInFund1",
      key: "ptHoldingInFund1",
      render: (val) => `${val?.toFixed(2)}%`,
    },
    {
      title: `% in ${modalData?.fund2Name || "Fund 2"}`,
      dataIndex: "ptHoldingInFund2",
      key: "ptHoldingInFund2",
      render: (val) => `${val?.toFixed(2)}%`,
    },
    {
      title: "Combined",
      key: "combined",
      render: (_, record) =>
        `${(record.ptHoldingInFund1 + record.ptHoldingInFund2).toFixed(2)}%`,
    },
  ];
  const compareTableColumns=[
    {
        title:"Stock Name",
        dataIndex:"stockName",
        key:"stockName",
        width:"40%",
    },
    {
        title:`% in ${compareResult?.fund1Name || "Fund 1"}`,
        dataIndex:"ptHoldingInFund1",
        key:"ptHoldingInFund1",
        width:"30%",
        render:(val)=>`${val?.toFixed(2)}%`,
    },
    {
        title:`% in ${compareResult?.fund2Name || "Fund 2"}`,
        dataIndex:"ptHoldingInFund2",
        key:"ptHoldingInFund2",
        width:"30%",
        render:(val)=>`${val?.toFixed(2)}%`,
    },

  ];

  // Portfolio Tab Content
  const portfolioTabContent = (
    <>
      <Card className="insights-section-card" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Select Holding Period</Title>
          <div className="insights-filter-group">
            <PeriodSelector
              holdingMonth={holdingMonth}
              holdingYear={holdingYear}
              onMonthChange={setHoldingMonth}
              onYearChange={setHoldingYear}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Select Funds</Title>
          <Select
            showSearch
            placeholder="Search and select funds..."
            style={{ width: "100%" }}
            onChange={handleFundSelect}
            value={null}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            loading={fundsLoading}
          >
            {allFunds
              .filter((f) => !selectedFunds.find((sf) => sf.id === f.id))
              .map((fund) => (
                <Select.Option key={fund.id} value={fund.id}>
                  {fund.name}
                </Select.Option>
              ))}
          </Select>
        </div>

        {selectedFunds.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Title level={5}>Selected Funds ({selectedFunds.length})</Title>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {selectedFunds.map((fund) => (
                <Tag
                  key={fund.id}
                  closable
                  onClose={() => handleFundRemove(fund.id)}
                  closeIcon={<CloseOutlined />}
                  style={{
                    padding: "6px 12px",
                    fontSize: 14,
                    borderRadius: 8,
                  }}
                >
                  {fund.name}
                </Tag>
              ))}
            </div>
          </div>
        )}

        <Button
          type="primary"
          size="large"
          onClick={generateOverlapReport}
          disabled={selectedFunds.length < 2 || loading}
          loading={loading}
          style={{ marginTop: 16 }}
        >
          Generate Overlap Report
        </Button>
      </Card>

      {overlapMatrix.length > 0 && (
        <Card className="insights-section-card">
          <Title level={4} style={{ marginBottom: 16 }}>
            Portfolio Overlap
          </Title>
          <Table
            columns={columns}
            dataSource={overlapMatrix}
            rowKey={(record) => `${record.fund1Id}-${record.fund2Id}`}
            pagination={false}
          />
        </Card>
      )}
    </>
  );

  // Compare Tab Content
  const compareTabContent = (
    <>
      <Card className="insights-section-card" style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 16 }}>
          Select Funds to Compare
        </Title>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 500, fontSize: 14 }}>
                Holding Period
              </label>
            </div>
            <PeriodSelector
              holdingMonth={compareHoldingMonth}
              holdingYear={compareHoldingYear}
              onMonthChange={setCompareHoldingMonth}
              onYearChange={setCompareHoldingYear}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 500, fontSize: 14 }}>Fund 1</label>
            </div>
            <Select
              showSearch
              placeholder="Select Fund 1"
              value={compareFund1}
              onChange={setCompareFund1}
              style={{ width: "100%" }}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              loading={fundsLoading}
            >
              {allFunds
                .filter((f) => f.id !== compareFund2)
                .map((fund) => (
                  <Select.Option key={fund.id} value={fund.id}>
                    {fund.name}
                  </Select.Option>
                ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 500, fontSize: 14 }}>Fund 2</label>
            </div>
            <Select
              showSearch
              placeholder="Select Fund 2"
              value={compareFund2}
              onChange={setCompareFund2}
              style={{ width: "100%" }}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              loading={fundsLoading}
            >
              {allFunds
                .filter((f) => f.id !== compareFund1)
                .map((fund) => (
                  <Select.Option key={fund.id} value={fund.id}>
                    {fund.name}
                  </Select.Option>
                ))}
            </Select>
          </Col>
        </Row>

        <Button
          type="primary"
          size="large"
          onClick={handleCompareTwoFunds}
          disabled={!compareFund1 || !compareFund2 || compareLoading}
          loading={compareLoading}
          style={{ marginTop: 24 }}
        >
          Compare Funds
        </Button>
      </Card>

      {compareLoading && (
        <Card className="insights-section-card">
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        </Card>
      )}

      {!compareLoading && compareResult && (
        <>
          {/* Venn Diagram */}
          <Card className="insights-section-card" style={{ marginBottom: 24 }}>
            <VennDiagram
              fund1Name={compareResult.fund1Name}
              fund2Name={compareResult.fund2Name}
              overlapPercentage={parseFloat(compareResult.overlapPt)}
            />

            {/* Overlap Summary */}
            <div
              style={{
                marginTop: 32,
                padding: 24,
                backgroundColor: "#1f1f1f",
                borderRadius: 12,
                color: "white",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h2 style={{ margin: 0, color: "white", fontSize: 32 }}>
                    {compareResult.overlapPt}% Overlap
                  </h2>
                  <div style={{ marginTop: 16 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      />
                      <span style={{ fontSize: 14 }}>
                        {compareResult.stockCount} Stocks Overlapping in Mutual
                        Funds Schemes
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      />
                      <span style={{ fontSize: 14 }}>
                        Balanced mix of diversification.
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 1,
                  }}
                >
                  {getOverlapLabel(compareResult.overlapPt)}
                </div>
              </div>
            </div>
          </Card>

          {/* Stocks Table */}
          <Card className="insights-section-card">
            <Title level={4} style={{ marginBottom: 16 }}>
              Overlapping Stocks
            </Title>
            <Table
              columns={compareTableColumns}
              dataSource={compareResult.stocks}
              rowKey="stockName"
              pagination={true}
              scroll={{ y: 600 }}
            />
          </Card>
        </>
      )}
    </>
  );

  const tabItems = [
    {
      key: "portfolio",
      label: "Compare Funds in Portfolio",
      children: portfolioTabContent,
    },
    {
      key: "compare",
      label: "Compare Two Funds",
      children: compareTabContent,
    },
  ];

  return (
    <div className="insights-page">
      <div className="insights-header">
        <div className="insights-header-left">
          <Title level={3} className="insights-header-title">
            Portfolio Overlap Analysis
          </Title>
          <span className="insights-header-subtitle">
            Analyze fund overlaps to optimize your portfolio diversification.
          </span>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginBottom: 24 }}
      />

      <Modal
        title={
          modalData
            ? `${modalData.fund1Name} vs ${modalData.fund2Name}`
            : "Loading..."
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {modalData?.loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : modalData?.error ? (
          <p>Error loading overlap details.</p>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>Total Overlap: {modalData?.overlapPt}%</Title>
            </div>
            <Table
              columns={modalColumns}
              dataSource={modalData?.stocks || []}
              rowKey="stockName"
              pagination={{ pageSize: 10 }}
              scroll={{ y: 600 }}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default PortfolioOverlap;
